import React, { useState, useRef, useCallback, useEffect } from 'react'
import { InteractiveDialog, InstructionsPure, ButtonHLPure, FileUploadMessage } from '../../pures'
import { dataURLtoFile, rotateImage, validateImageFile } from '../../../utils/image.util'
import './modal-file-upload-crop.css'

/**
 * @function RotateIcon
 * @description Icono SVG para el botón de rotar imagen
 * @returns {JSX.Element}
 */
const RotateIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 4V9H9" stroke="#2c353c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5.5 15C6.2 17.8 8.8 20 12 20C15.9 20 19 16.9 19 13C19 9.1 15.9 6 12 6C9.5 6 7.3 7.3 6.1 9.3L4 4" stroke="#2c353c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/**
 * @function DeleteIcon
 * @description Icono SVG para el botón de eliminar imagen
 * @returns {JSX.Element}
 */
const DeleteIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polyline points="3,6 5,6 21,6" stroke="#2c353c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 6V20C19 21.1 18.1 22 17 22H7C5.9 22 5 21.1 5 20V6M8 6V4C8 2.9 8.9 2 10 2H14C15.1 2 16 2.9 16 4V6" stroke="#2c353c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/** @type {number} Tamaño mínimo del recuadro de recorte en px */
const MIN_CROP_SIZE = 50

/** @type {number} Tamaño de las manijas de esquina en px */
const HANDLE_SIZE = 10

/**
 * @typedef {Object} ModalFileUploadCropProps
 * @property {function} t - Función de traducción i18n
 * @property {function} onCropConfirm - Callback al confirmar el recorte. Recibe (syntheticEvent, documentSelected, dataURL)
 * @property {function} onClose - Callback para cerrar/cancelar (volver al modal anterior)
 * @property {Object} documentSelected - Documento seleccionado actualmente
 */

/**
 * @function ModalFileUploadCrop
 * @description Modal para cargar un archivo de imagen y ajustarla manualmente usando
 * un lienzo con recorte personalizado. Incluye funciones de rotar y eliminar.
 * @param {ModalFileUploadCropProps} props
 * @returns {JSX.Element}
 */
const ModalFileUploadCrop = ({ t, onCropConfirm, onClose, documentSelected }) => {
  /** @type {['upload'|'crop', function]} Fase actual del flujo */
  const [phase, setPhase] = useState('upload')
  /** @type {[string|null, function]} Data URL de la imagen cargada */
  const [imageSrc, setImageSrc] = useState(null)
  /** @type {[string|null, function]} Mensaje de error */
  const [errorMessage, setErrorMessage] = useState(null)

  // ─── Crop state ────────────────────────────────────────────
  /** @type {[{x: number, y: number, w: number, h: number}, function]} Rectángulo de recorte */
  const [cropRect, setCropRect] = useState({ x: 0, y: 0, w: 0, h: 0 })
  /** @type {React.MutableRefObject<HTMLCanvasElement>} Ref al canvas de recorte */
  const canvasRef = useRef(null)
  /** @type {React.MutableRefObject<HTMLImageElement>} Ref al objeto Image */
  const imageObjRef = useRef(null)
  /** @type {React.MutableRefObject<string|null>} Tipo de arrastre activo */
  const dragTypeRef = useRef(null)
  /** @type {React.MutableRefObject<{x: number, y: number}>} Posición inicial del drag */
  const dragStartRef = useRef({ x: 0, y: 0 })
  /** @type {React.MutableRefObject<{x: number, y: number, w: number, h: number}>} CropRect al inicio del drag */
  const cropStartRef = useRef({ x: 0, y: 0, w: 0, h: 0 })
  /** @type {React.MutableRefObject<{scaleX: number, scaleY: number}>} Escala entre imagen real y canvas */
  const scaleRef = useRef({ scaleX: 1, scaleY: 1 })

  /**
   * @function handleFileSelected
   * @description Maneja la selección de archivo (desde input o drag-and-drop)
   * @param {Event} event - Evento del input file
   * @returns {void}
   */
  const handleFileSelected = useCallback((event) => {
    setErrorMessage(null)
    const file = event.target.files && event.target.files[0]
    if (!file) return

    const maxMB = Number(process.env.REACP_APP_MB_UPLOAD_IMAGE_IDENTITY) || 10
    const validation = validateImageFile(file, maxMB)

    if (!validation.valid) {
      const errorKey = validation.error === 'invalidFormat'
        ? 'uploadIdentityDocument.modals.fileUpload.invalidFormat'
        : 'uploadIdentityDocument.modals.fileUpload.maxSize'
      setErrorMessage(t(errorKey))
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      setImageSrc(e.target.result)
      setPhase('crop')
    }
    reader.readAsDataURL(file)
  }, [t])

  /**
   * @function initializeCropCanvas
   * @description Inicializa el canvas cuando cambia la imagen cargada
   */
  useEffect(() => {
    if (phase !== 'crop' || !imageSrc) return

    const img = new Image()
    img.onload = () => {
      imageObjRef.current = img
      const canvas = canvasRef.current
      if (!canvas) return

      // Calcular dimensiones del canvas respetando el contenedor
      const container = canvas.parentElement
      const maxW = container.clientWidth - 16
      const maxH = 460

      let canvasW = img.width
      let canvasH = img.height

      if (canvasW > maxW) {
        const ratio = maxW / canvasW
        canvasW = maxW
        canvasH = canvasH * ratio
      }
      if (canvasH > maxH) {
        const ratio = maxH / canvasH
        canvasH = maxH
        canvasW = canvasW * ratio
      }

      canvas.width = canvasW
      canvas.height = canvasH

      scaleRef.current = {
        scaleX: img.width / canvasW,
        scaleY: img.height / canvasH
      }

      // Crop inicial: 85% centrado (simulando el marco de INE)
      const cropW = canvasW * 0.85
      const cropH = canvasH * 0.7
      const initialCrop = {
        x: (canvasW - cropW) / 2,
        y: (canvasH - cropH) / 2,
        w: cropW,
        h: cropH
      }
      setCropRect(initialCrop)
      drawCanvas(img, canvasW, canvasH, initialCrop)
    }
    img.src = imageSrc
  }, [phase, imageSrc])

  /**
   * @function drawCanvas
   * @description Dibuja la imagen y el overlay de recorte en el canvas
   * @param {HTMLImageElement} img - Imagen a dibujar
   * @param {number} cw - Ancho del canvas
   * @param {number} ch - Alto del canvas
   * @param {{x: number, y: number, w: number, h: number}} crop - Rectángulo de recorte
   * @returns {void}
   */
  const drawCanvas = useCallback((img, cw, ch, crop) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')

    // Dibujar imagen completa
    ctx.clearRect(0, 0, cw, ch)
    ctx.drawImage(img, 0, 0, cw, ch)

    // Overlay semi-transparente fuera del crop
    ctx.fillStyle = 'rgba(0, 0, 0, 0.4)'
    // Top
    ctx.fillRect(0, 0, cw, crop.y)
    // Bottom
    ctx.fillRect(0, crop.y + crop.h, cw, ch - crop.y - crop.h)
    // Left
    ctx.fillRect(0, crop.y, crop.x, crop.h)
    // Right
    ctx.fillRect(crop.x + crop.w, crop.y, cw - crop.x - crop.w, crop.h)

    // Borde punteado del crop
    ctx.setLineDash([8, 5])
    ctx.strokeStyle = '#00cc44'
    ctx.lineWidth = 3
    ctx.strokeRect(crop.x, crop.y, crop.w, crop.h)
    ctx.setLineDash([])

    // Esquinas sólidas
    const cl = 20
    ctx.strokeStyle = '#00cc44'
    ctx.lineWidth = 4
    const corners = [
      // Top-left
      [[crop.x, crop.y + cl], [crop.x, crop.y], [crop.x + cl, crop.y]],
      // Top-right
      [[crop.x + crop.w - cl, crop.y], [crop.x + crop.w, crop.y], [crop.x + crop.w, crop.y + cl]],
      // Bottom-left
      [[crop.x, crop.y + crop.h - cl], [crop.x, crop.y + crop.h], [crop.x + cl, crop.y + crop.h]],
      // Bottom-right
      [[crop.x + crop.w - cl, crop.y + crop.h], [crop.x + crop.w, crop.y + crop.h], [crop.x + crop.w, crop.y + crop.h - cl]],
    ]
    corners.forEach(([start, mid, end]) => {
      ctx.beginPath()
      ctx.moveTo(...start)
      ctx.lineTo(...mid)
      ctx.lineTo(...end)
      ctx.stroke()
    })

    // Manijas de esquina (cuadraditos)
    ctx.fillStyle = '#00cc44'
    const hs = HANDLE_SIZE
    const handles = [
      [crop.x - hs / 2, crop.y - hs / 2],
      [crop.x + crop.w - hs / 2, crop.y - hs / 2],
      [crop.x - hs / 2, crop.y + crop.h - hs / 2],
      [crop.x + crop.w - hs / 2, crop.y + crop.h - hs / 2],
    ]
    handles.forEach(([hx, hy]) => {
      ctx.fillRect(hx, hy, hs, hs)
    })
  }, [])

  /**
   * @function redrawCurrent
   * @description Redibuja el canvas con el crop actual
   * @returns {void}
   */
  const redrawCurrent = useCallback(() => {
    if (!imageObjRef.current || !canvasRef.current) return
    drawCanvas(imageObjRef.current, canvasRef.current.width, canvasRef.current.height, cropRect)
  }, [cropRect, drawCanvas])

  useEffect(() => {
    if (phase === 'crop' && imageObjRef.current) {
      redrawCurrent()
    }
  }, [cropRect, phase, redrawCurrent])

  // ─── Mouse/Touch event handlers ───────────────────────────
  /**
   * @function getCanvasPos
   * @description Obtiene la posición del cursor relativa al canvas
   * @param {MouseEvent|Touch} e
   * @returns {{cx: number, cy: number}}
   */
  const getCanvasPos = useCallback((e) => {
    const canvas = canvasRef.current
    if (!canvas) return { cx: 0, cy: 0 }
    const rect = canvas.getBoundingClientRect()
    const scaleDisplayX = canvas.width / rect.width
    const scaleDisplayY = canvas.height / rect.height
    return {
      cx: (e.clientX - rect.left) * scaleDisplayX,
      cy: (e.clientY - rect.top) * scaleDisplayY
    }
  }, [])

  /**
   * @function getDragType
   * @description Determina el tipo de interacción según posición del cursor
   * @param {number} cx - Posición X en el canvas
   * @param {number} cy - Posición Y en el canvas
   * @returns {string|null} Tipo de drag: 'move', 'nw', 'ne', 'sw', 'se', o null
   */
  const getDragType = useCallback((cx, cy) => {
    const c = cropRect
    const hs = HANDLE_SIZE + 4

    // Esquinas
    if (Math.abs(cx - c.x) < hs && Math.abs(cy - c.y) < hs) return 'nw'
    if (Math.abs(cx - (c.x + c.w)) < hs && Math.abs(cy - c.y) < hs) return 'ne'
    if (Math.abs(cx - c.x) < hs && Math.abs(cy - (c.y + c.h)) < hs) return 'sw'
    if (Math.abs(cx - (c.x + c.w)) < hs && Math.abs(cy - (c.y + c.h)) < hs) return 'se'

    // Dentro del crop → mover
    if (cx >= c.x && cx <= c.x + c.w && cy >= c.y && cy <= c.y + c.h) return 'move'

    return null
  }, [cropRect])

  /**
   * @function handlePointerDown
   * @description Inicia interacción de arrastre
   * @param {React.MouseEvent|React.TouchEvent} e
   * @returns {void}
   */
  const handlePointerDown = useCallback((e) => {
    e.preventDefault()
    const point = e.touches ? e.touches[0] : e
    const { cx, cy } = getCanvasPos(point)
    const type = getDragType(cx, cy)
    if (!type) return

    dragTypeRef.current = type
    dragStartRef.current = { x: cx, y: cy }
    cropStartRef.current = { ...cropRect }
  }, [getCanvasPos, getDragType, cropRect])

  /**
   * @function handlePointerMove
   * @description Actualiza el crop durante el arrastre
   * @param {React.MouseEvent|React.TouchEvent} e
   * @returns {void}
   */
  const handlePointerMove = useCallback((e) => {
    if (!dragTypeRef.current) return
    e.preventDefault()

    const point = e.touches ? e.touches[0] : e
    const { cx, cy } = getCanvasPos(point)
    const dx = cx - dragStartRef.current.x
    const dy = cy - dragStartRef.current.y
    const cs = cropStartRef.current
    const canvas = canvasRef.current
    if (!canvas) return

    let newCrop = { ...cs }

    if (dragTypeRef.current === 'move') {
      newCrop.x = Math.max(0, Math.min(cs.x + dx, canvas.width - cs.w))
      newCrop.y = Math.max(0, Math.min(cs.y + dy, canvas.height - cs.h))
    } else {
      // Redimensionar desde esquinas
      if (dragTypeRef.current.includes('w')) {
        const newX = Math.max(0, cs.x + dx)
        const newW = cs.w - (newX - cs.x)
        if (newW >= MIN_CROP_SIZE) {
          newCrop.x = newX
          newCrop.w = newW
        }
      }
      if (dragTypeRef.current.includes('e')) {
        const newW = Math.min(canvas.width - cs.x, cs.w + dx)
        if (newW >= MIN_CROP_SIZE) {
          newCrop.w = newW
        }
      }
      if (dragTypeRef.current.includes('n')) {
        const newY = Math.max(0, cs.y + dy)
        const newH = cs.h - (newY - cs.y)
        if (newH >= MIN_CROP_SIZE) {
          newCrop.y = newY
          newCrop.h = newH
        }
      }
      if (dragTypeRef.current.includes('s')) {
        const newH = Math.min(canvas.height - cs.y, cs.h + dy)
        if (newH >= MIN_CROP_SIZE) {
          newCrop.h = newH
        }
      }
    }

    setCropRect(newCrop)
  }, [getCanvasPos])

  /**
   * @function handlePointerUp
   * @description Finaliza interacción de arrastre
   * @returns {void}
   */
  const handlePointerUp = useCallback(() => {
    dragTypeRef.current = null
  }, [])

  /**
   * @function handleRotate
   * @description Rota la imagen 90 grados en sentido horario
   * @returns {void}
   */
  const handleRotate = useCallback(async () => {
    if (!imageSrc) return
    try {
      const rotated = await rotateImage(imageSrc)
      setImageSrc(rotated)
    } catch (error) {
      console.error('Error al rotar imagen:', error)
    }
  }, [imageSrc])

  /**
   * @function handleDelete
   * @description Elimina la imagen cargada y vuelve al estado de carga
   * @returns {void}
   */
  const handleDelete = useCallback(() => {
    setImageSrc(null)
    setCropRect({ x: 0, y: 0, w: 0, h: 0 })
    imageObjRef.current = null
    setPhase('upload')
    setErrorMessage(null)
  }, [])

  /**
   * @function handleConfirmCrop
   * @description Confirma el recorte y envía la imagen recortada al flujo principal
   * @returns {void}
   */
  const handleConfirmCrop = useCallback(() => {
    if (!imageObjRef.current || !canvasRef.current) return

    const scale = scaleRef.current
    const realCrop = {
      x: cropRect.x * scale.scaleX,
      y: cropRect.y * scale.scaleY,
      w: cropRect.w * scale.scaleX,
      h: cropRect.h * scale.scaleY
    }

    // Canvas temporal para el recorte
    const tmpCanvas = document.createElement('canvas')
    tmpCanvas.width = realCrop.w
    tmpCanvas.height = realCrop.h
    const tmpCtx = tmpCanvas.getContext('2d')
    tmpCtx.drawImage(
      imageObjRef.current,
      realCrop.x, realCrop.y, realCrop.w, realCrop.h,
      0, 0, realCrop.w, realCrop.h
    )

    const croppedDataURL = tmpCanvas.toDataURL('image/jpeg', 0.92)
    const file = dataURLtoFile(croppedDataURL, `INE_crop_${Date.now()}.jpg`)
    const syntheticEvent = {
      target: {
        files: [file]
      }
    }
    onCropConfirm(syntheticEvent, documentSelected, croppedDataURL)
  }, [cropRect, documentSelected, onCropConfirm])

  /** @type {Array<string>} Instrucciones para FileUploadMessage */
  const uploadInstructions = [
    t('uploadIdentityDocument.modals.fileUpload.instructions')
  ]

  return (
    <InteractiveDialog
      title={t('uploadIdentityDocument.modals.fileUpload.title')}
      onClose={onClose}
      dialogSize="G"
      CustomButtons={
        <div className="modalFileUploadCrop-buttons">
          <ButtonHLPure
            nameBtn={t('button.cancel')}
            onClickBtn={onClose}
          />
          {phase === 'crop' && (
            <ButtonHLPure
              nameBtn={t('uploadIdentityDocument.modals.fileUpload.confirmCrop')}
              primary
              onClickBtn={handleConfirmCrop}
            />
          )}
        </div>
      }
    >
      <div className="modalFileUploadCrop-container">
        <InstructionsPure instructions={uploadInstructions} />

        {errorMessage && (
          <p className="modalFileUploadCrop-error">{errorMessage}</p>
        )}

        {phase === 'upload' && (
          <FileUploadMessage
            onChange={handleFileSelected}
            acceptFiles="image/jpeg,image/jpg"
            uploadText={t('uploadIdentityDocument.modals.fileUpload.dragOrUpload')}
            enableDragAndDrop
            inputId="ine-file-upload"
          />
        )}

        {phase === 'crop' && (
          <>
            <div className="modalFileUploadCrop-canvas-wrapper">
              <canvas
                ref={canvasRef}
                className="modalFileUploadCrop-canvas"
                onMouseDown={handlePointerDown}
                onMouseMove={handlePointerMove}
                onMouseUp={handlePointerUp}
                onMouseLeave={handlePointerUp}
                onTouchStart={handlePointerDown}
                onTouchMove={handlePointerMove}
                onTouchEnd={handlePointerUp}
              />
            </div>

            <div className="modalFileUploadCrop-controls">
              <button
                type="button"
                className="modalFileUploadCrop-control-btn"
                onClick={handleRotate}
              >
                <RotateIcon />
                {t('uploadIdentityDocument.modals.fileUpload.rotate')}
              </button>

              <button
                type="button"
                className="modalFileUploadCrop-control-btn"
                onClick={handleDelete}
              >
                <DeleteIcon />
                {t('uploadIdentityDocument.modals.fileUpload.delete')}
              </button>
            </div>
          </>
        )}
      </div>
    </InteractiveDialog>
  )
}

export { ModalFileUploadCrop as default }
